import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  ArrowLeft, ChevronRight, Pencil, Pause, Play, MoreHorizontal, Copy, Check,
  Mail, Phone, Globe, MapPin, Building2, UserSquare, Activity, Receipt,
  History, Crown, ShieldCheck, User, Clock, CheckCircle2, CircleSlash,
  BarChart3, Inbox, Sparkles, CreditCard, LayoutDashboard, TrendingUp,
  AlertTriangle, Trash2, Loader2, KeyRound, Star,
  Briefcase, IdCard, BookOpen, Hash,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import PlanValidityCard from '../../components/billing/PlanValidityCard';
import PaymentHistoryCard from '../../components/billing/PaymentHistoryCard';
import { userService } from '../../services';

/**
 * User Detail Page (Super Admin)
 * View detailed information about a user. Tabs: overview / contact /
 * billing / activity. Solo trainers and clients get billing content;
 * everyone else sees an "org handles billing" empty state.
 */

// Shared chrome — kept in lockstep with OrganizationDetailPage so the
// two detail pages read as one product surface.
const HERO_CARD =
  'rounded-3xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.10)]';
const PANEL_CARD =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.06)] p-6 lg:p-7';
const META_STRIP =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 divide-x divide-neutral-200/60 dark:divide-neutral-800 grid grid-cols-2 sm:grid-cols-4';

const HERO_GRADIENTS = [
  'from-primary-500 to-primary-700',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-600',
  'from-cyan-500 to-sky-600',
];
const seededHeroGradient = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) & 0x7fffffff;
  return HERO_GRADIENTS[hash % HERO_GRADIENTS.length];
};

const STATUS_CHIP = {
  active:    { Icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30' },
  suspended: { Icon: Pause,        cls: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30' },
  expired:   { Icon: Clock,        cls: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30' },
  inactive:  { Icon: CircleSlash,  cls: 'bg-neutral-50 text-neutral-700 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700' },
};

// Role chip palette — same hues as the list page role chips.
const ROLE_CHIP = {
  'super-admin':       { label: 'Super Admin',       cls: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30',    text: 'text-rose-600 dark:text-rose-400' },
  client:              { label: 'Client',            cls: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/30', text: 'text-violet-600 dark:text-violet-400' },
  trainer:             { label: 'Trainer',           cls: 'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/30',    text: 'text-cyan-600 dark:text-cyan-400' },
  individual_trainer:  { label: 'Individual Trainer',cls: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30', text: 'text-amber-600 dark:text-amber-400' },
  participant:         { label: 'Participant',       cls: 'bg-neutral-50 text-neutral-700 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700', text: 'text-neutral-600 dark:text-neutral-400' },
};
const roleChip = (role, isIndependentTrainer) => {
  if (role === 'trainer' && isIndependentTrainer) return ROLE_CHIP.individual_trainer;
  return ROLE_CHIP[role] || ROLE_CHIP.participant;
};

const PLAN_PILL = {
  free:     'bg-neutral-50 text-neutral-700 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700',
  pro:      'bg-primary-50 text-primary-700 ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30',
  pro_plus: 'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/30',
};

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};
const formatRelative = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMs / 3600000);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffDay <= 14) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return formatDate(d);
};

const getRoleLabel = (role) => {
  const map = {
    'super-admin': 'Super Admin',
    client: 'Client',
    trainer: 'Trainer',
    participant: 'Participant',
  };
  return map[role] || role;
};

const UserDetailPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [subscriptionRecord, setSubscriptionRecord] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [participantLimit, setParticipantLimit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, loading: false });
  const [showMore, setShowMore] = useState(false);
  const [copiedKey, setCopiedKey] = useState('');

  const moreRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await userService.getUser(userId);
      const userData = response.data || response;
      setSubscriptionRecord(userData.subscriptionRecord || null);
      setPaymentHistory(userData.paymentHistory || []);
      setParticipantLimit(userData.participantLimit || null);

      setUser({
        id: userData._id,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phone || '',
        avatar: userData.avatar || null,
        role: userData.role || 'participant',
        organizationId: userData.organization?._id || userData.primaryOrganization?._id || '',
        organizationName: userData.organization?.name || userData.primaryOrganization?.name || '',
        organizationType: userData.organization?.type || userData.primaryOrganization?.type || 'standard',
        status: userData.status || 'active',
        department: userData.department || '',
        jobTitle: userData.jobTitle || '',
        bio: userData.bio || '',
        expertise: userData.expertise || [],
        emailVerified: userData.emailVerified ?? null,
        twoFactorEnabled: userData.twoFactorEnabled ?? null,
        lastPasswordChange: userData.lastPasswordChange || null,
        lastLoginAt: userData.lastLogin || userData.lastLoginAt || null,
        createdAt: userData.createdAt || new Date().toISOString(),
        totalSessions: userData.totalSessions || 0,
        completedSessions: userData.completedSessions || 0,
        avgRating: userData.avgRating || 0,
        plan: userData.organization?.plan || userData.primaryOrganization?.plan || userData.plan || 'free',
        subscriptionStatus: userData.organization?.subscriptionStatus || userData.primaryOrganization?.subscriptionStatus || userData.subscriptionStatus || 'active',
        planRenewalDate: userData.organization?.renewalDate || userData.primaryOrganization?.renewalDate || userData.planRenewalDate || null,
        // Usage Statistics tab — only populated for solo trainers (backend
        // attaches these fields under userController.getUser when role='trainer'
        // and primaryOrg.type='individual'). Other users get the Billing
        // empty-state and don't see this tab.
        usage: userData.usage || null,
        historicalUsage: userData.historicalUsage || null,
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Close the More popover when clicking outside.
  useEffect(() => {
    if (!showMore) return undefined;
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setShowMore(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showMore]);

  const handleCopy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(''), 1200);
    } catch {
      // silent
    }
  };

  const openDeleteModal = () => { setDeleteModal({ isOpen: true, loading: false }); setShowMore(false); };
  const closeDeleteModal = () => setDeleteModal({ isOpen: false, loading: false });

  const handleDeleteConfirm = async () => {
    setDeleteModal((prev) => ({ ...prev, loading: true }));
    try {
      await userService.deleteUser(userId);
      toast.success(`${user?.firstName ? `${user.firstName} ${user.lastName}`.trim() : 'User'} has been deleted`);
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // ────── Loading state ──────
  if (loading || !user) {
    return (
      <DashboardLayout role="super-admin">
        <div className="w-full px-4 lg:px-6 py-6 space-y-4">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100">
            <ArrowLeft size={14} /> Back
          </button>
          {/* Flat header skeleton — no card surface */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-neutral-200 dark:bg-neutral-800 animate-pulse flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-5 w-1/2 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
              <div className="h-3 w-2/3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
            </div>
          </div>
          <div className={META_STRIP}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="px-5 py-4 space-y-2">
                <div className="h-2.5 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse w-1/2" />
                <div className="h-5 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ────── Derived state ──────
  const isIndependentTrainer = user.role === 'trainer' && user.organizationType === 'individual';
  const isSuspended = user.status === 'suspended';
  const effectiveStatus = isSuspended ? 'suspended' : (user.status || 'inactive');
  const fullName = `${user.firstName} ${user.lastName}`.trim() || user.email || 'User';
  const initials = `${(user.firstName || '').charAt(0)}${(user.lastName || '').charAt(0)}`.toUpperCase() || '?';
  const heroGradient = seededHeroGradient(initials || 'AA');
  const role = roleChip(user.role, isIndependentTrainer);
  const statusChip = STATUS_CHIP[effectiveStatus] || STATUS_CHIP.inactive;
  const planPillCls = PLAN_PILL[user.plan] || PLAN_PILL.free;
  const billingApplicable = user.role === 'client' || isIndependentTrainer;

  // "Member since" with same-day awareness — same logic as
  // OrganizationDetailPage for consistency in the meta strip.
  const memberSince = (() => {
    const created = new Date(user.createdAt);
    if (Number.isNaN(created.getTime())) return '—';
    const days = Math.floor((Date.now() - created.getTime()) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return formatDate(user.createdAt);
  })();

  // Usage / Billing / Activity tabs are only shown for solo (individual)
  // trainers, since they own a personal subscription. For clients and
  // org-employed trainers, this data lives at the org level — the admin sees
  // it on the Organization detail page instead.
  const showSoloOnlyTabs = isIndependentTrainer;

  const tabs = [
    { id: 'overview', label: 'Overview',            Icon: LayoutDashboard },
    { id: 'contact',  label: 'Contact Information', Icon: UserSquare },
    ...(showSoloOnlyTabs
      ? [
          { id: 'usage',    label: 'Usage Statistics',  Icon: BarChart3 },
          { id: 'billing',  label: 'Billing History',   Icon: Receipt, count: paymentHistory?.length || null },
          { id: 'activity', label: 'Activity Timeline', Icon: History,  count: user.totalSessions || null },
        ]
      : []),
  ];

  const panelMotion = prefersReducedMotion
    ? { initial: false, animate: { opacity: 1 } }
    : { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.18 } };

  return (
    <DashboardLayout role="super-admin">
      <div className="w-full px-4 lg:px-6 py-6 space-y-4">
        {/* ────── Back ────── */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 group transition-colors"
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
          Back
        </button>

        {/* ────── Header (flat — no card surface) ────── */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-1">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <div className="relative flex-shrink-0">
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={fullName}
                    className="w-10 h-10 rounded-xl object-cover ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800"
                  />
                ) : (
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 text-white font-semibold text-base ring-1 ring-inset ring-primary-300/30 shadow-[inset_0_-2px_4px_rgba(0,0,0,0.08)]"
                    aria-hidden="true"
                  >
                    {initials}
                  </div>
                )}
                {effectiveStatus === 'active' && (
                  <span aria-hidden="true" className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-white dark:ring-neutral-900" />
                )}
              </div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50 truncate">
                {fullName}
              </h1>
              {/* Plan chip — only for users that own a plan (solo trainers /
                  clients). Mirrors the org page header so admins see the same
                  shape on both pages. */}
              {billingApplicable && subscriptionRecord?.plan && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset capitalize ${PLAN_PILL[subscriptionRecord.plan] || PLAN_PILL.free}`}>
                  {(subscriptionRecord.plan || 'free').replace('_', ' ')} Plan
                </span>
              )}
              {/* Status chip — solid outlined pill */}
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset capitalize ${statusChip.cls}`}>
                <statusChip.Icon size={12} strokeWidth={1.75} />
                {effectiveStatus}
              </span>
              {/* Role chip */}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${role.cls}`}>
                {role.label}
              </span>
            </div>
            <p className="flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
              <Mail size={12} strokeWidth={1.75} />
              {user.email ? (
                <a href={`mailto:${user.email}`} className="hover:text-primary-600 dark:hover:text-primary-400 truncate transition-colors">
                  {user.email}
                </a>
              ) : (
                <span className="truncate">—</span>
              )}
            </p>
          </div>

          {/* Action cluster */}
          <div className="flex items-center gap-2 flex-shrink-0 relative" ref={moreRef}>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/users/${userId}/edit`)}>
              <Pencil size={14} strokeWidth={1.75} className="mr-1.5" />
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowMore((s) => !s)}>
              <MoreHorizontal size={14} strokeWidth={1.75} />
            </Button>
            {showMore && (
              <div className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 shadow-lg p-1 z-20">
                <button
                  type="button"
                  onClick={openDeleteModal}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                >
                  <Trash2 size={14} strokeWidth={1.75} />
                  Delete user
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ────── Meta strip ────── */}
        <div className={META_STRIP}>
          <div className="px-5 py-4 flex flex-col gap-0.5">
            <p className="text-[11px] uppercase tracking-[0.06em] text-neutral-400 dark:text-neutral-500 font-semibold">Role</p>
            <p className={`text-lg font-semibold tabular-nums ${role.text}`}>{role.label}</p>
          </div>
          <div className="px-5 py-4 flex flex-col gap-0.5">
            <p className="text-[11px] uppercase tracking-[0.06em] text-neutral-400 dark:text-neutral-500 font-semibold">Organization</p>
            <p className="text-lg font-semibold tabular-nums text-neutral-900 dark:text-neutral-100 truncate">
              {user.organizationName || (isIndependentTrainer ? 'Independent' : '—')}
            </p>
          </div>
          <div className="px-5 py-4 flex flex-col gap-0.5">
            <p className="text-[11px] uppercase tracking-[0.06em] text-neutral-400 dark:text-neutral-500 font-semibold">Last login</p>
            <p className="text-lg font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{formatRelative(user.lastLoginAt)}</p>
          </div>
          <div className="px-5 py-4 flex flex-col gap-0.5">
            <p className="text-[11px] uppercase tracking-[0.06em] text-neutral-400 dark:text-neutral-500 font-semibold">Member since</p>
            <p className="text-lg font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{memberSince}</p>
          </div>
        </div>

        {/* ────── Sticky tab bar — segmented pill bar with icons ────── */}
        <div className="sticky top-0 z-10 -mx-4 px-4 lg:-mx-6 lg:px-6 py-2 bg-neutral-50/70 dark:bg-neutral-950/60 backdrop-blur-md border-b border-neutral-200/70 dark:border-neutral-800/70">
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)] overflow-x-auto">
            {tabs.map(({ id, label, Icon: TabIcon, count }) => {
              const active = activeTab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`px-3.5 py-2 rounded-lg text-[15px] font-medium transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
                    active
                      ? 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30'
                      : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100'
                  }`}
                >
                  {TabIcon && <TabIcon size={16} strokeWidth={1.75} />}
                  {label}
                  {count != null && count > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                      active
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-500/20 dark:text-primary-200'
                        : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ────── Tab panels ────── */}
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} {...panelMotion} className="space-y-6">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
                <div className={`xl:col-span-7 ${PANEL_CARD}`}>
                  <SectionHeader Icon={User} title="Profile" subtitle="Identity, role, and where they belong." />
                  {(() => {
                    // Build the row list dynamically — only include rows
                    // with values, so we never render placeholder dashes.
                    const rows = [
                      fullName && { Icon: User, label: 'Full name', value: fullName },
                      user.email && {
                        Icon: Mail, label: 'Email', value: user.email,
                        href: `mailto:${user.email}`,
                        copyable: true, copied: copiedKey === 'profile-email',
                        onCopy: () => handleCopy('profile-email', user.email),
                      },
                      user.phone && {
                        Icon: Phone, label: 'Phone', value: user.phone,
                        href: `tel:${user.phone}`,
                        copyable: true, copied: copiedKey === 'profile-phone',
                        onCopy: () => handleCopy('profile-phone', user.phone),
                      },
                      { Icon: ShieldCheck, label: 'Role', value: role.label, valueClassName: role.text },
                      (user.organizationName || isIndependentTrainer) && {
                        Icon: Building2,
                        label: 'Organization',
                        value: user.organizationName || 'Independent',
                        href: user.organizationId && !isIndependentTrainer ? `/admin/organizations/${user.organizationId}` : null,
                      },
                      user.department && { Icon: Briefcase, label: 'Department', value: user.department },
                      user.jobTitle && { Icon: IdCard, label: 'Job title', value: user.jobTitle },
                      isIndependentTrainer && {
                        Icon: Star, label: 'Independent trainer', value: 'Yes',
                        valueClassName: 'text-amber-600 dark:text-amber-400',
                      },
                    ].filter(Boolean);
                    return <div>{rows.map((r, i) => <InfoRow key={i} {...r} />)}</div>;
                  })()}

                  {isIndependentTrainer && (
                    <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 ring-1 ring-inset ring-amber-200 dark:ring-amber-500/20">
                      <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                        This trainer registered independently and is not part of any client organization. Their personal organization handles their own subscription and billing.
                      </p>
                    </div>
                  )}

                  {user.role === 'trainer' && user.bio && (
                    <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800/70">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <BookOpen size={12} strokeWidth={1.75} className="text-neutral-400" />
                        <p className="text-[11px] uppercase tracking-[0.06em] text-neutral-400 dark:text-neutral-500 font-semibold">Bio</p>
                      </div>
                      <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed max-w-prose whitespace-pre-line">{user.bio}</p>
                    </div>
                  )}

                  {user.role === 'trainer' && user.expertise && user.expertise.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800/70">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Sparkles size={12} strokeWidth={1.75} className="text-neutral-400" />
                        <p className="text-[11px] uppercase tracking-[0.06em] text-neutral-400 dark:text-neutral-500 font-semibold">Expertise</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {user.expertise.map((tag, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset bg-white dark:bg-neutral-900 ring-neutral-200 dark:ring-neutral-700 text-neutral-700 dark:text-neutral-200">
                            <Sparkles size={10} strokeWidth={1.75} className="text-primary-500" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA when only the always-on rows render. */}
                  {!user.email && !user.phone && !user.department && !user.jobTitle && !user.bio && (!user.expertise || user.expertise.length === 0) && (
                    <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800/70 flex items-center justify-between gap-3">
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">Add more profile details from the edit page.</p>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/users/${userId}/edit`)}>
                        <Pencil size={14} strokeWidth={1.75} className="mr-1.5" />
                        Edit profile
                      </Button>
                    </div>
                  )}
                </div>

                <div className={`xl:col-span-5 ${PANEL_CARD}`}>
                  <SectionHeader Icon={ShieldCheck} title="Account" subtitle="Status, verification, and login history." />
                  <div>
                    <InfoRow
                      Icon={statusChip.Icon}
                      label="Status"
                      value={effectiveStatus}
                      valueClassName="capitalize"
                    />
                    {/* "Email verified" row removed — the platform doesn't gate
                        features on verification (most users never click the
                        link, and email delivery isn't reliable in all envs),
                        so the row mostly shows "Not verified" for legit users
                        and reads as misleading noise on this page. The
                        emailVerified field still exists on the User document
                        for any future flow that wants to use it. */}
                    {user.twoFactorEnabled !== null && (
                      <InfoRow
                        Icon={ShieldCheck}
                        label="Two-factor authentication"
                        value={user.twoFactorEnabled ? 'Enabled' : 'Not enabled'}
                        valueClassName={user.twoFactorEnabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-500 dark:text-neutral-400'}
                      />
                    )}
                    <InfoRow Icon={Clock} label="Last login" value={formatRelative(user.lastLoginAt)} />
                    <InfoRow Icon={History} label="Created on" value={formatDate(user.createdAt)} />
                    {user.lastPasswordChange && (
                      <InfoRow Icon={KeyRound} label="Last password change" value={formatRelative(user.lastPasswordChange)} />
                    )}
                    {user.failedLoginAttempts != null && user.failedLoginAttempts > 0 && (
                      <InfoRow
                        Icon={AlertTriangle}
                        label="Failed login attempts"
                        value={String(user.failedLoginAttempts)}
                        valueClassName="text-amber-600 dark:text-amber-400 tabular-nums"
                      />
                    )}
                    {user.id && (
                      <InfoRow
                        Icon={Hash}
                        label="Account ID"
                        value={user.id}
                        valueClassName="font-mono text-xs"
                        copyable
                        copied={copiedKey === 'account-id'}
                        onCopy={() => handleCopy('account-id', user.id)}
                      />
                    )}
                  </div>

                  {/* Mini "Recent activity" tail — keeps the right column
                      visually anchored to the same vertical extent as
                      the left when there isn't much account metadata. */}
                  {(() => {
                    const events = [
                      user.lastLoginAt && { dot: 'bg-violet-400', label: 'Logged in', date: user.lastLoginAt },
                      user.lastPasswordChange && { dot: 'bg-amber-400', label: 'Changed password', date: user.lastPasswordChange },
                      { dot: 'bg-emerald-400', label: 'Account created', date: user.createdAt },
                    ].filter(Boolean).slice(0, 3);
                    if (events.length === 0) return null;
                    return (
                      <div className="border-t border-neutral-100 dark:border-neutral-800/70 mt-4 pt-4">
                        <p className="text-[11px] uppercase tracking-[0.06em] text-neutral-400 dark:text-neutral-500 font-semibold mb-2">Recent activity</p>
                        {events.map((e, i) => (
                          <div key={i} className="flex items-center gap-2 py-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${e.dot}`} aria-hidden="true" />
                            <span className="text-sm text-neutral-700 dark:text-neutral-200">{e.label}</span>
                            <span className="ml-auto text-xs text-neutral-400 dark:text-neutral-500 tabular-nums">{formatRelative(e.date)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className={PANEL_CARD}>
                <SectionHeader Icon={UserSquare} title="Contact information" subtitle="Reach this user directly." />
                <div>
                  <InfoRow
                    Icon={Mail}
                    label="Email"
                    value={user.email || '—'}
                    href={user.email ? `mailto:${user.email}` : null}
                    copyable={!!user.email}
                    copied={copiedKey === 'email'}
                    onCopy={() => handleCopy('email', user.email)}
                  />
                  <InfoRow
                    Icon={Phone}
                    label="Phone"
                    value={user.phone || '—'}
                    href={user.phone ? `tel:${user.phone}` : null}
                    copyable={!!user.phone}
                    copied={copiedKey === 'phone'}
                    onCopy={() => handleCopy('phone', user.phone)}
                  />
                  <InfoRow Icon={UserSquare} label="Job title" value={user.jobTitle || '—'} />
                  <InfoRow Icon={Building2} label="Department" value={user.department || '—'} />
                </div>

                {user.email && (
                  <div className="mt-5 pt-5 border-t border-neutral-100 dark:border-neutral-800/70">
                    <a
                      href={`mailto:${user.email}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors"
                    >
                      <Mail size={14} strokeWidth={1.75} />
                      Send message
                    </a>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'usage' && showSoloOnlyTabs && (
              <div className="space-y-6">
                {/* Plan Limits & Usage — mirrors OrganizationDetailPage so admins
                    see the same shape whether they're on an org or a solo trainer. */}
                <div className={PANEL_CARD}>
                  <SectionHeader Icon={BarChart3} title="Plan Limits & Usage" subtitle="Current period consumption against plan caps." />
                  <div className="space-y-6">
                    {[
                      { key: 'sessions', label: 'Total Sessions', barCls: 'bg-blue-500',   unit: '' },
                      { key: 'trainers', label: 'Trainers',       barCls: 'bg-emerald-500', unit: '' },
                    ].map(({ key, label, barCls, unit }) => {
                      const slice = user.usage?.[key] || { used: 0, limit: 0 };
                      const unlimited = slice.limit === -1 || !slice.limit;
                      const pct = unlimited
                        ? 10
                        : Math.min((slice.used / slice.limit) * 100, 100);
                      return (
                        <div key={key}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{label}</span>
                            <span className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                              {unlimited
                                ? `${slice.used}${unit} / Unlimited`
                                : `${slice.used}${unit} / ${slice.limit}${unit}`}
                            </span>
                          </div>
                          <div className="w-full bg-neutral-200 dark:bg-neutral-800 rounded-full h-3">
                            <div className={`${barCls} h-3 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Historical Usage — last 30 days vs the 30 days before that. */}
                <div className={PANEL_CARD}>
                  <SectionHeader Icon={TrendingUp} title="Historical Usage (Last 30 Days)" subtitle="Trend versus the previous 30-day window." />
                  {(() => {
                    const h = user.historicalUsage || {
                      sessionsLast30Days: 0, sessionsChange: 0,
                      participantsLast30Days: 0, participantsChange: 0,
                      engagementLast30Days: 0, engagementChange: 0,
                    };
                    const tiles = [
                      { label: 'Sessions Conducted', value: h.sessionsLast30Days, change: h.sessionsChange, suffix: '' },
                      { label: 'Total Participants', value: (h.participantsLast30Days || 0).toLocaleString(), change: h.participantsChange, suffix: '' },
                      { label: 'Avg. Engagement',    value: h.engagementLast30Days, change: h.engagementChange, suffix: '%' },
                    ];
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {tiles.map((t) => (
                          <div key={t.label} className="p-4 rounded-xl ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800">
                            <p className="text-xs sm:text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2">{t.label}</p>
                            <p className="text-xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-50 tabular-nums">{t.value}{t.suffix}</p>
                            <p className={`text-xs mt-1 ${t.change >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                              {t.change >= 0 ? '+' : ''}{t.change}{t.suffix} from previous month
                            </p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {activeTab === 'billing' && showSoloOnlyTabs && (
              <div className="space-y-6">
                {billingApplicable ? (
                  <>
                    <div className={PANEL_CARD}>
                      {(() => {
                        // When the plan has lapsed, label it as the previous
                        // plan rather than current — same wording as the org
                        // page so admins see consistent copy across both.
                        const subEnd = subscriptionRecord?.currentPeriodEnd;
                        const subStatus = subscriptionRecord?.status;
                        const isExpired =
                          subStatus === 'expired' ||
                          subStatus === 'past_due' ||
                          (subEnd && new Date(subEnd).getTime() < Date.now());
                        return (
                          <SectionHeader
                            Icon={CreditCard}
                            title={isExpired ? 'Previous Plan' : 'Subscription'}
                            subtitle={isExpired ? 'Last active plan and dates.' : 'Current plan and renewal.'}
                          />
                        );
                      })()}
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 capitalize">
                          {user.plan?.replace('_', ' ') || 'Free'}
                        </p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${planPillCls}`}>
                          {user.plan?.replace('_', ' ').toUpperCase() || 'FREE'}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 capitalize">
                        Status: {user.subscriptionStatus || 'Active'}
                      </p>
                      {user.planRenewalDate && (
                        <p className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                          <Clock size={12} strokeWidth={1.75} />
                          {user.plan === 'free'
                            ? `Plan ends ${formatDate(user.planRenewalDate)}`
                            : `Renews ${formatDate(user.planRenewalDate)}`}
                        </p>
                      )}
                    </div>

                    {user.organizationId && (
                      <PlanValidityCard
                        context="trainer"
                        organizationId={user.organizationId}
                        subscriptionRecord={subscriptionRecord}
                        onExtended={fetchUser}
                        participantLimit={participantLimit}
                        onOverrideChanged={fetchUser}
                      />
                    )}

                    <PaymentHistoryCard paymentHistory={paymentHistory} />
                  </>
                ) : (
                  <div className={PANEL_CARD}>
                    <EmptyState
                      Icon={CreditCard}
                      title="This user doesn't manage their own billing"
                      description="Their organization handles plan and invoices."
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activity' && showSoloOnlyTabs && (
              <div className={PANEL_CARD}>
                <SectionHeader Icon={History} title="Activity" subtitle="Recent events on this account." />
                <div className="relative">
                  {/* Newest at top, oldest at bottom — Account created sits
                      at the bottom of the timeline since it's the earliest
                      event on the record. */}
                  {[
                    user.totalSessions > 0 && {
                      type: 'session',
                      title: `Sessions conducted`,
                      time: '',
                      details: `${user.totalSessions} total · ${user.completedSessions} completed${user.avgRating > 0 ? ` · ${user.avgRating}/5.0 avg rating` : ''}`,
                    },
                    user.lastLoginAt && { type: 'user', title: 'Last login', time: formatRelative(user.lastLoginAt) },
                    { type: 'organization', title: 'Account created', time: formatDate(user.createdAt) },
                  ].filter(Boolean).map((a, idx, arr) => {
                    const tone = ACTIVITY_TONE[a.type] || ACTIVITY_TONE.session;
                    const ToneIcon = tone.Icon;
                    const isLast = idx === arr.length - 1;
                    return (
                      <div key={idx} className="relative flex gap-4 pb-6 last:pb-0">
                        {!isLast && (
                          <div aria-hidden="true" className="absolute left-[19px] top-11 bottom-0 w-px bg-gradient-to-b from-neutral-200 via-neutral-200 to-transparent dark:from-neutral-800 dark:via-neutral-800" />
                        )}
                        <div className={`relative z-10 w-10 h-10 rounded-xl ring-1 ring-inset flex items-center justify-center flex-shrink-0 ${tone.cls}`}>
                          <ToneIcon size={16} strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0 pt-1">
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{a.title}</p>
                          {a.time && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{a.time}</p>}
                          {a.details && (
                            <div className="mt-2 px-3 py-2 rounded-lg bg-neutral-50/70 dark:bg-neutral-900/60 ring-1 ring-inset ring-neutral-200/60 dark:ring-neutral-800">
                              <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">{a.details}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ────── Delete modal (rose) ────── */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        title="Delete User"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-rose-50 ring-1 ring-inset ring-rose-200 dark:bg-rose-500/10 dark:ring-rose-500/20 flex-shrink-0">
              <AlertTriangle size={20} strokeWidth={1.75} className="text-rose-600 dark:text-rose-400" />
            </div>
            <div>
              <p className="text-sm text-neutral-700 dark:text-neutral-200">
                Are you sure you want to delete <span className="font-semibold">{fullName}</span>?
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                This action cannot be undone. The user will lose access to their account and all associated data will be permanently removed.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-neutral-200 dark:border-neutral-800">
            <button
              type="button"
              onClick={closeDeleteModal}
              disabled={deleteModal.loading}
              className="px-3 py-2 rounded-xl text-sm font-medium ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteConfirm}
              disabled={deleteModal.loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-rose-600 hover:bg-rose-700 text-white shadow-[0_4px_12px_-4px_rgba(244,63,94,0.45)] transition-colors disabled:opacity-60"
            >
              {deleteModal.loading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={1.75} />}
              {deleteModal.loading ? 'Deleting…' : 'Delete User'}
            </button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

// ────── Reusable subcomponents (file-local) ──────
// Identical to the OrganizationDetailPage versions — keep them in lockstep.

const ACTIVITY_TONE = {
  organization: { Icon: Building2, cls: 'bg-blue-50 text-blue-600 ring-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20' },
  user:         { Icon: User,      cls: 'bg-violet-50 text-violet-600 ring-violet-100 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-500/20' },
  session:      { Icon: Activity,  cls: 'bg-emerald-50 text-emerald-600 ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20' },
  billing:      { Icon: Receipt,   cls: 'bg-amber-50 text-amber-600 ring-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20' },
  suspension:   { Icon: AlertTriangle, cls: 'bg-rose-50 text-rose-600 ring-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-500/20' },
};

const SectionHeader = ({ Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-4">
    <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 text-neutral-600 dark:text-neutral-400">
      <Icon size={16} strokeWidth={1.75} />
    </span>
    <div>
      <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 leading-tight">{title}</h2>
      {subtitle && <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const InfoRow = ({ Icon, label, value, href, copyable, copied, onCopy, valueClassName, multiline }) => (
  <div className="flex items-center gap-3 py-3 border-b last:border-0 border-neutral-100 dark:border-neutral-800/70 group">
    <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700 flex-shrink-0">
      <Icon size={14} strokeWidth={1.75} />
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-xs uppercase tracking-[0.04em] text-neutral-400 dark:text-neutral-500">{label}</p>
      {href ? (
        <a
          href={href}
          target={href.startsWith('http') ? '_blank' : undefined}
          rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
          className={`text-base font-medium hover:text-primary-600 dark:hover:text-primary-400 truncate transition-colors ${valueClassName || 'text-neutral-900 dark:text-neutral-100'}`}
        >
          {value}
        </a>
      ) : (
        <p className={`text-base font-medium ${value === '—' ? 'text-neutral-400 dark:text-neutral-500' : valueClassName || 'text-neutral-900 dark:text-neutral-100'} ${multiline ? 'whitespace-pre-line' : 'truncate'}`}>
          {value}
        </p>
      )}
    </div>
    {copyable && (
      <button
        type="button"
        onClick={onCopy}
        aria-label={copied ? 'Copied' : 'Copy'}
        className={`flex-shrink-0 transition-opacity ${copied ? 'opacity-100 text-emerald-500' : 'opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200'}`}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    )}
  </div>
);

const EmptyState = ({ Icon, title, description }) => (
  <div className="text-center py-10">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800">
      <Icon size={22} strokeWidth={1.75} className="text-neutral-400" />
    </div>
    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mt-3">{title}</p>
    {description && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 max-w-sm mx-auto">{description}</p>}
  </div>
);

export default UserDetailPage;
